import { t } from '@avalara/bat-apps-shared/i18n';
import { SAlert } from '@avalara/skylab-sdk/react';
import { type FormEvent, useRef, useState } from 'react';

import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { setAviatorCrushLeadCookie } from '../../lib/aviator-crush-cookie';
import { type AviatorCrushLeadFieldName, isAviatorCrushLeadFieldValid } from '../../lib/aviator-crush-lead-validation';
import { setAviatorCrushPromptCookie } from '../../lib/aviator-crush-prompt-cookie';
import { submitAviatorCrushSignup } from '../../lib/side-effects/aviator-crush';
import { FieldErrorMessage, FormInput } from '../styled/components/sign-up';
import { FormNavButtons } from '../styled/skylab/page';

type FieldName = AviatorCrushLeadFieldName;

type FormState = Record<FieldName, string>;
type TouchedState = Record<FieldName, boolean>;

const INITIAL_FORM_STATE: FormState = { firstName: '', lastName: '', email: '' };
const INITIAL_TOUCHED_STATE: TouchedState = { firstName: false, lastName: false, email: false };

const isFieldValid = isAviatorCrushLeadFieldValid;

const isFormValid = (form: FormState): boolean =>
  (Object.keys(form) as Array<FieldName>).every((fieldName) => isFieldValid(fieldName, form[fieldName]));

const getNamedInputValue = (form: HTMLFormElement, fieldName: FieldName): string => {
  const field = form.elements.namedItem(fieldName);
  return field instanceof HTMLInputElement ? field.value : '';
};

const submittedFormToFormState = (form: HTMLFormElement): FormState => ({
  email: getNamedInputValue(form, 'email'),
  firstName: getNamedInputValue(form, 'firstName'),
  lastName: getNamedInputValue(form, 'lastName'),
});

type LeadFormProps = {
  /** From the QR-code landing URL's `crushPrompt` query param (PF-8767). Carried through
   * signup as-is so it can survive into the aviator-crush chat composer. */
  crushPrompt?: string;
  // Prefills fields from a lead cookie (PF-8766) that either wasn't complete enough to
  // auto-submit, or was complete but whose auto-submit failed - lets a returning attendee finish
  // or retry the form instead of retyping everything.
  initialValues?: Partial<FormState>;
  // Set when this form is being shown *because* a cookie-driven auto-submit already failed
  // (AviatorCrushLanding) - shows the retry alert immediately instead of making the attendee
  // resubmit once just to see it.
  initialSubmitFailed?: boolean;
};

const LeadForm = ({ crushPrompt, initialValues, initialSubmitFailed = false }: LeadFormProps = {}) => {
  const dispatch = useAppDispatch();
  const [form, setForm] = useState<FormState>({ ...INITIAL_FORM_STATE, ...initialValues });
  const [touched, setTouched] = useState<TouchedState>(INITIAL_TOUCHED_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [postSubmitFailed, setPostSubmitFailed] = useState(initialSubmitFailed);

  // Guards against rapid repeat taps firing duplicate requests before the `submitting`
  // state update has re-rendered the disabled button (AC: double-submit protection).
  const submissionInFlight = useRef(false);

  const handleFieldChange = (fieldName: FieldName) => (event: FormEvent<HTMLInputElement>) => {
    // Read the value synchronously - `event.currentTarget` is nullified once the handler
    // returns, but the setForm updater below may run later, after that happens.
    const value = event.currentTarget.value;
    setForm((current) => ({ ...current, [fieldName]: value }));
  };

  const handleFieldBlur = (fieldName: FieldName) => () => {
    setTouched((current) => ({ ...current, [fieldName]: true }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setTouched({ firstName: true, lastName: true, email: true });
    if (submissionInFlight.current) return;

    const submittedForm = submittedFormToFormState(event.currentTarget);
    // Autofill/password managers can write straight to the DOM input without firing React's
    // onChange, leaving `form` state stale. Sync it to the same snapshot used for validation so
    // fieldError() below can't disagree with what's actually being submitted.
    setForm(submittedForm);
    if (!isFormValid(submittedForm)) return;

    submissionInFlight.current = true;
    setSubmitting(true);
    setPostSubmitFailed(false);

    try {
      const body = crushPrompt ? { ...submittedForm, crushPrompt } : submittedForm;
      const result = await submitAviatorCrushSignup(dispatch, body);

      if (result.ok) {
        // Set only after the 200 - a submission that fails validation/provisioning must not skip
        // the form (and re-send to MILES) on the attendee's next visit.
        setAviatorCrushLeadCookie(submittedForm);
        // crushPrompt rides in a cookie, not the magic-link URL (PF-8804) - Identity's
        // redirect_uri allowlist matches exactly, so an arbitrary prompt value can't ride in
        // clientAppUrl.
        if (crushPrompt) setAviatorCrushPromptCookie(crushPrompt);
        window.location.href = result.value.redirectUrl;
        return;
      }
    } catch {
      // submitAviatorCrushSignup's own fetch call converts network/transport failures into a
      // Result, but getOwdBearerToken (awaited above it) can still reject outright - without
      // this, that leaves submitting/submissionInFlight stuck true forever with no way to retry.
    }

    submissionInFlight.current = false;
    setSubmitting(false);
    setPostSubmitFailed(true);
  };

  const errorId = (fieldName: FieldName) => `aviator-crush-${fieldName}-error`;

  // margin-all-none cancels FieldErrorMessage's own negative margin-top (a hack elsewhere
  // that overlaps the error into an input's reserved bottom padding) - with each field in
  // its own fieldset here, that hack instead shrinks the gap to the next field unevenly.
  const fieldError = (fieldName: FieldName) =>
    touched[fieldName] && !isFieldValid(fieldName, form[fieldName]) ? (
      <FieldErrorMessage id={errorId(fieldName)} className='margin-all-none pad-top-xs'>
        <p>{t(`client.aviatorCrush.errors.${fieldName}-invalid`)}</p>
      </FieldErrorMessage>
    ) : null;

  return (
    <form onSubmit={handleSubmit} noValidate>
      <fieldset className='margin-bottom-sm'>
        <label htmlFor='aviator-crush-first-name' className='required'>
          {t('client.aviatorCrush.first-name-label')}
        </label>
        <FormInput
          id='aviator-crush-first-name'
          name='firstName'
          type='text'
          value={form.firstName}
          onChange={handleFieldChange('firstName')}
          onBlur={handleFieldBlur('firstName')}
          isInvalid={touched.firstName && !isFieldValid('firstName', form.firstName)}
          aria-required='true'
          aria-invalid={touched.firstName && !isFieldValid('firstName', form.firstName)}
          aria-describedby={errorId('firstName')}
          marginAboveError=''
          disabled={submitting}
          autoComplete='given-name'
          autoFocus
        />
        {fieldError('firstName')}
      </fieldset>
      <fieldset className='margin-bottom-sm'>
        <label htmlFor='aviator-crush-last-name' className='required'>
          {t('client.aviatorCrush.last-name-label')}
        </label>
        <FormInput
          id='aviator-crush-last-name'
          name='lastName'
          type='text'
          value={form.lastName}
          onChange={handleFieldChange('lastName')}
          onBlur={handleFieldBlur('lastName')}
          isInvalid={touched.lastName && !isFieldValid('lastName', form.lastName)}
          aria-required='true'
          aria-invalid={touched.lastName && !isFieldValid('lastName', form.lastName)}
          aria-describedby={errorId('lastName')}
          marginAboveError=''
          disabled={submitting}
          autoComplete='family-name'
        />
        {fieldError('lastName')}
      </fieldset>
      <fieldset>
        <label htmlFor='aviator-crush-email' className='required'>
          {t('client.labels.email-address')}
        </label>
        <FormInput
          id='aviator-crush-email'
          name='email'
          type='email'
          value={form.email}
          onChange={handleFieldChange('email')}
          onBlur={handleFieldBlur('email')}
          isInvalid={touched.email && !isFieldValid('email', form.email)}
          aria-required='true'
          aria-invalid={touched.email && !isFieldValid('email', form.email)}
          aria-describedby={errorId('email')}
          marginAboveError=''
          disabled={submitting}
          autoComplete='email'
        />
        {fieldError('email')}
      </fieldset>

      {postSubmitFailed && (
        <SAlert className='show-icon margin-top-sm margin-bottom-sm' status='error' noDismiss>
          <div data-ref='aviator-crush.lead-form.submit-error'>
            {t('client.errors.generic-title')}. {t('client.aviatorCrush.retry-message')}
          </div>
        </SAlert>
      )}

      <FormNavButtons>
        <button type='submit' className='primary large' disabled={submitting} data-ref='aviator-crush.lead-form.submit'>
          {submitting ? t('client.aviatorCrush.submitting') : t('client.aviatorCrush.submit-button')}
        </button>
      </FormNavButtons>
    </form>
  );
};

export default LeadForm;
